import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Check, Plus, ChevronLeft, ChevronRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { formatUSD } from "@/lib/api";
import { toast } from "sonner";

const badgeColor = (badge) => {
  if (badge === "Most Popular") return "bg-brand-red text-white";
  if (badge === "Premium") return "bg-brand-navy text-white";
  return "bg-white text-brand-navy border border-brand-line";
};

export const ProductCard = ({ product, index = 0 }) => {
  const { addItem } = useCart();
  const navigate = useNavigate();
  const testId = product.slug;
  const gallery = product.gallery?.length ? product.gallery : [product.image];
  const [idx, setIdx] = useState(0);
  const touchX = useRef(null);

  const step = (n, e) => {
    e?.stopPropagation();
    setIdx((i) => (i + n + gallery.length) % gallery.length);
  };

  const handleAdd = (e) => {
    e.stopPropagation();
    addItem(product);
    toast.success(`${product.name} added to cart`, {
      description: "Free US shipping · No long-term contract",
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 34 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`product-card-${testId}`}
      className="group flex flex-col rounded-3xl bg-white border border-brand-line shadow-card hover:shadow-cardHover hover:-translate-y-1.5 transition-all duration-300 overflow-hidden"
    >
      {/* swipeable gallery frame */}
      <div
        onClick={() => navigate(`/products/${product.slug}`)}
        onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          if (touchX.current == null) return;
          const dx = e.changedTouches[0].clientX - touchX.current;
          touchX.current = null;
          if (Math.abs(dx) > 40) setIdx((i) => (i + (dx < 0 ? 1 : -1) + gallery.length) % gallery.length);
        }}
        data-testid={`product-image-${testId}`}
        className="relative aspect-square overflow-hidden cursor-pointer bg-[radial-gradient(circle_at_50%_30%,#ffffff,#EDF1F6)]"
      >
        {product.badge && (
          <span
            className={`absolute top-4 left-4 z-10 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide ${badgeColor(
              product.badge
            )}`}
          >
            {product.badge}
          </span>
        )}
        <span className="absolute top-4 right-4 z-10 px-3 py-1.5 rounded-full bg-brand-navy/90 text-white text-xs font-semibold">
          {product.highlight}
        </span>
        {gallery.map((src, i) => (
          <img
            key={src}
            src={src}
            alt={`${product.name} — photo ${i + 1}`}
            loading="lazy"
            className={`absolute inset-0 w-full h-full transition-opacity duration-300 ${
              i === 0 ? "object-contain p-6" : "object-cover"
            } ${i === idx ? "opacity-100" : "opacity-0"}`}
          />
        ))}
        {gallery.length > 1 && (
          <>
            <button
              onClick={(e) => step(-1, e)}
              data-testid={`gallery-prev-${testId}`}
              aria-label="Previous photo"
              className="btn-focus absolute left-2 top-1/2 -translate-y-1/2 z-10 grid place-items-center w-9 h-9 rounded-full bg-white/95 text-brand-navy shadow-card opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
            >
              <ChevronLeft className="w-5 h-5" strokeWidth={2.6} />
            </button>
            <button
              onClick={(e) => step(1, e)}
              data-testid={`gallery-next-${testId}`}
              aria-label="Next photo"
              className="btn-focus absolute right-2 top-1/2 -translate-y-1/2 z-10 grid place-items-center w-9 h-9 rounded-full bg-white/95 text-brand-navy shadow-card opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
            >
              <ChevronRight className="w-5 h-5" strokeWidth={2.6} />
            </button>
            <div className="absolute bottom-3 inset-x-0 z-10 flex justify-center gap-1.5">
              {gallery.map((_, i) => (
                <button
                  key={i}
                  onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                  aria-label={`Photo ${i + 1}`}
                  className={`w-2 h-2 rounded-full transition-colors ${i === idx ? "bg-brand-red" : "bg-brand-navy/25 hover:bg-brand-navy/50"}`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <div className="flex flex-col flex-1 p-6">
        <span className="text-xs font-bold uppercase tracking-[0.16em] text-brand-red">
          {product.category}
        </span>
        <Link to={`/products/${product.slug}`} data-testid={`product-title-${testId}`} className="btn-focus">
          <h3 className="mt-2 font-display font-extrabold text-2xl text-brand-navy leading-tight hover:text-brand-red transition-colors">
            {product.name}
          </h3>
        </Link>
        <p className="mt-2 text-brand-ink2 leading-relaxed">{product.tagline}</p>

        <ul className="mt-5 space-y-2.5">
          {product.features.slice(0, 3).map((f, i) => (
            <li key={i} className="flex items-start gap-2.5 text-[15px] text-brand-navy">
              <Check className="w-5 h-5 shrink-0 text-brand-red mt-0.5" strokeWidth={2.6} />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        <Link
          to={`/products/${product.slug}`}
          data-testid={`product-details-${testId}`}
          className="btn-focus mt-4 inline-flex items-center gap-1 text-sm font-bold text-brand-navy hover:text-brand-red transition-colors"
        >
          Full specs & photos <ChevronRight className="w-4 h-4" strokeWidth={2.8} />
        </Link>

        <div className="mt-5 pt-5 border-t border-brand-line flex items-end justify-between gap-3">
          <div>
            <span className="block text-xs text-brand-ink2 font-semibold">One-time · Free shipping</span>
            <span className="font-display font-extrabold text-3xl text-brand-navy">
              {formatUSD(product.price)}
            </span>
          </div>
          <button
            onClick={handleAdd}
            data-testid={`add-to-cart-${testId}`}
            className="btn-focus inline-flex items-center gap-2 h-13 min-h-[52px] px-5 rounded-full bg-brand-red text-white font-bold hover:bg-brand-red-600 transition-colors"
          >
            <Plus className="w-5 h-5" strokeWidth={2.8} />
            Add
          </button>
        </div>
      </div>
    </motion.div>
  );
};
